/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabriele
 * File: SelectRecipePanel.tsx
 * Project: mle-client-n
 * Committed last: 2022/6/13 @ 146
 * ---
 * Description:
 */

import {useCallback, useEffect, useState} from 'react';
import {MyLabelCompanyMeta, MyLabelRevision} from '../../../../entities';
import {mleDomainGqlClient} from '../../../../api/domain';
import {ML_LOGIN} from '../../../../api/domain/mutations.gql';
import {MyLabelAuthFeature} from '../../../../store/features/mylabel/auth/MyLabelAuthFeature';
import {GET_COMPANIES_META} from '../../../../api/domain/queries.gql';
import {NoRevisionCallout} from '../../../common/callout/NoRevisionCallout';
import {Callout, Checkbox, Icon, Intent} from '@blueprintjs/core';
import {CompanySelect} from "../../select/data-origin/CompanySelect";
import {CompanyRevisionsSelect} from "../../select/data-origin/CompanyRevisionsSelect";

export interface RecipeSelection {
    company: MyLabelCompanyMeta;
    revision: MyLabelRevision;
}

interface Props {
    onSelectionChange: (selection?: RecipeSelection) => void;
    setCanCreate: (value: boolean) => void;
    setCreateLoading: (value: boolean) => void;
    myLabelConnected: boolean;
}

export const SelectRevisionPanel = (props: Props) => {
    const [disableRevisions, setDisableRevisions] = useState<boolean>(MyLabelAuthFeature.requiresCredentials());
    const [disableRevisionSwitch, setDisableRevisionSwitch] = useState(MyLabelAuthFeature.requiresCredentials());
    const [selectedRevision, setSelectedRevision] = useState<MyLabelRevision>();
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
    const [isLoadingRecipes, setIsLoadingRecipes] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<MyLabelCompanyMeta>();
    const [revisions, setRevisions] = useState<MyLabelRevision[]>([]);
    const [companies, setCompanies] = useState<MyLabelCompanyMeta[]>([]);
    const [noRevision, setNoRevision] = useState(false);

    useEffect(() => {
        if (disableRevisions) {
            props.onSelectionChange(undefined);
            props.setCanCreate(true);
            props.setCreateLoading(false);
        }
    }, [props, disableRevisions])

    useEffect(() => {
            (async () => {
                if (disableRevisions && disableRevisionSwitch) {
                    props.onSelectionChange(undefined);
                    props.setCanCreate(true);
                    return;
                }

                // Carico il contenuto
                await loadContent();
            })();
        },
        [disableRevisions, disableRevisionSwitch]);

    /**
     * Carica le informazioni del pannello
     */
    const loadContent = useCallback(async () => {
        setIsLoadingCompanies(true);
        setNoRevision(false);
        props.setCreateLoading(true);
        const key = window.localStorage.getItem(MyLabelAuthFeature.__usr_key);
        const pwd = window.localStorage.getItem(MyLabelAuthFeature.__usr_pwd);

        await mleDomainGqlClient.mutate({
            mutation: ML_LOGIN,
            variables: {
                username: key,
                password: pwd
            }
        });

        const allCompanies = (await mleDomainGqlClient.query({
            query: GET_COMPANIES_META,
            fetchPolicy: 'no-cache'
        })).data.getCompaniesMeta as MyLabelCompanyMeta[];

        if (allCompanies && allCompanies.length > 0)
            setSelectedCompany(allCompanies[allCompanies.length - 1]);

        setCompanies(allCompanies);
        props.setCreateLoading(false);
        setIsLoadingCompanies(false);
    }, [setNoRevision, setIsLoadingCompanies, setSelectedCompany,
        setSelectedRevision, setRevisions, setCompanies, props.setCanCreate, setNoRevision])

    const handleCompanyChange = useCallback(value => {
        setSelectedCompany(value);
        setSelectedRevision(undefined);
        props.onSelectionChange({
            company: value,
            revision: selectedRevision as MyLabelRevision
        });
    }, [setSelectedCompany, setSelectedRevision]);

    const handleRevisionSelect = useCallback((value: MyLabelRevision) => {
        setSelectedRevision(value);
        props.onSelectionChange({
            company: selectedCompany as MyLabelCompanyMeta,
            revision: value
        });

        if (value)
            props.setCanCreate(true);
    }, [setSelectedRevision, selectedCompany]);

    const handleNoRevisions = useCallback(() => {
        setNoRevision(true);
        props.setCreateLoading(false);
        props.setCanCreate(false);
    }, [setNoRevision, selectedCompany]);

    const withRevisionContent = (
        <div>
            {
                !isLoadingRecipes && !isLoadingCompanies && noRevision &&
                <NoRevisionCallout orgName={selectedCompany?.name || ''}
                                   className="mb-4"/>
            }
            <div className="text-xl">Scegli una revisione attiva per il progetto
            </div>
            <div className="bp4-text-muted mb-2">Seleziona l'organizzazione
                MyLabel
                attiva per il nuovo progetto
            </div>
            <CompanySelect companies={companies}
                           onChange={handleCompanyChange}
                           isLoading={isLoadingCompanies}
                           initialValue={selectedCompany}/>
            <div className="text-xl mb-2 mt-4">Revisione MyLabel</div>
            <div className="bp4-text-muted mb-2">Seleziona la revisione MyLabel
                attiva
                per
                il progetto che stai creando o <a target="_blank"
                                                  href="http://beta.mylabel.it/it/schede/labelrevisions/?dsid=&has_version=1">
                    vedi elenco revisioni disponibili su MyLabel <Icon
                    icon="backlink" className="mr-1"/>
                </a>
            </div>
            {selectedCompany && <CompanyRevisionsSelect
                selectedRevision={selectedRevision}
                onRevisionLoaded={(data) => {
                    setNoRevision(data.length == 0);
                    props.setCreateLoading(false);
                    setIsLoadingRecipes(false);
                }}
                onStartLoading={() => {
                    setIsLoadingRecipes(true);
                    setNoRevision(false);
                    props.setCreateLoading(true);
                }}
                onNoRevision={handleNoRevisions}
                companyId={selectedCompany?.id as number}
                onRevisionSelect={handleRevisionSelect}
            />}
        </div>
    )

    const noRevisionContent = (
        <Callout intent={Intent.WARNING}>
            Attenzione: tutte le origini dati presenti nell'etichetta verranno perse. Sarà necessario re-inserirle a
            mano successivamente.
        </Callout>
    )

    return (
        <div className="p-4">
            <Checkbox checked={disableRevisions} disabled={disableRevisionSwitch} label="Non ho delle revisioni"
                      onChange={event => setDisableRevisions(event.currentTarget.checked)}/>
            {
                !disableRevisions ? withRevisionContent : noRevisionContent
            }
        </div>
    );
};
